<script setup lang="ts">
/**
 * A SCREEN THAT IS NOT BUILT YET, AND SAYS SO.
 *
 * It renders from a static descriptor and nothing else. This file and its
 * descriptors may not import the API client, and `test/preview-isolation.spec.ts`
 * fails the build if they do — so a sketch physically cannot display a real
 * number beside an invented one.
 *
 * The banner is not decoration. A customer must be able to tell, at a glance
 * and without being told, which screens are the product and which are the plan;
 * anything less is a demo that sells something that does not exist.
 */

import type { Sketch } from "./sketches";

defineProps<{
  sketch: Sketch;
  t: (key: string) => string;
}>();
</script>

<template>
  <section>
    <div class="sketch__banner" role="note">
      <strong class="sketch__badge">{{ t("preview.badge") }}</strong>
      <span>{{ t("preview.note") }}</span>
    </div>

    <p class="sketch__blurb">{{ sketch.blurb }}</p>

    <p v-if="sketch.blocker" class="gf-note gf-note--warn">{{ sketch.blocker }}</p>

    <div class="gf-tiles sketch__dim">
      <div v-for="tile in sketch.tiles" :key="tile.label" class="sketch__tile">
        <span class="sketch__tileLabel">{{ tile.label }}</span>
        <strong class="sketch__tileValue">{{ tile.value }}</strong>
      </div>
    </div>

    <div class="gf-panel sketch__dim">
      <table class="gf-table">
        <thead>
          <tr>
            <th
              v-for="column in sketch.columns"
              :key="column.label"
              scope="col"
              :class="column.align === 'end' ? 'gf-num' : ''"
            >
              {{ column.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in sketch.rows" :key="index">
            <td
              v-for="(cell, cellIndex) in row"
              :key="cellIndex"
              :class="sketch.columns[cellIndex]?.align === 'end' ? 'gf-num' : ''"
            >
              {{ cell }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- `sketch.module` ("M9") stays in the data as the planning trail; it is
         the product plan's code, not a word a restaurateur uses. -->
  </section>
</template>

<style scoped>
.sketch__banner {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--gf-space-3);
  padding: var(--gf-space-3) var(--gf-space-4);
  border: var(--gf-border-thin) dashed var(--gf-color-warning);
  border-radius: var(--gf-radius-lg);
  background: var(--gf-color-warning-surface);
  color: var(--gf-color-text);
  margin-bottom: var(--gf-space-4);
}

/* A status pill, sentence case — the words say "mock-up", not the capitals. */
.sketch__badge {
  padding: 0 var(--gf-space-2);
  font-size: var(--gf-text-xs);
  font-weight: var(--gf-weight-semibold);
  line-height: 1.6;
  color: var(--gf-color-warning);
  background: var(--gf-color-surface);
  border-radius: var(--gf-radius-full);
}

.sketch__blurb {
  margin: 0 0 var(--gf-space-4);
  max-width: 62ch;
  font-size: var(--gf-text-md);
  line-height: var(--gf-leading-normal);
  color: var(--gf-color-text);
}

/*
  Held back visually as well as verbally. A sketch that looks exactly like a
  built screen relies entirely on somebody reading the banner.
*/
.sketch__dim {
  opacity: 0.62;
}

.sketch__tile {
  display: flex;
  flex-direction: column;
  gap: var(--gf-space-0);
  padding: var(--gf-space-4) var(--gf-space-5);
  border: var(--gf-border-thin) dashed var(--gf-color-border);
  border-radius: var(--gf-radius-lg);
}

.sketch__tileLabel {
  font-size: var(--gf-text-sm);
  font-weight: var(--gf-weight-medium);
  color: var(--gf-color-text-muted);
}

.sketch__tileValue {
  font-size: var(--gf-text-2xl);
  line-height: 1.1;
  color: var(--gf-color-text-muted);
}

</style>
